import React, { useRef, useEffect, useLayoutEffect, useReducer, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { themeColors } from '@theme/classNames';

const PORTAL_VIEWPORT_MARGIN = 8;

// Define initial state
const dropdownItemInitialState = {
    isHovered: false,
    isPressed: false
};

// Define reducer function
const dropdownItemReducer = (state, action) => {
    switch (action.type) {
        case 'SET_HOVER':
            return { ...state, isHovered: action.payload };
        case 'SET_PRESSED':
            return { ...state, isPressed: action.payload };
        default:
            return state;
    }
};

const DropdownItem = ({ onClick, children, className = '', title, 'data-testid': dataTestId }) => {
    // Use reducer
    const [state, dispatch] = useReducer(dropdownItemReducer, dropdownItemInitialState);

    // Handle mouse events
    const handleMouseEnter = () => dispatch({ type: 'SET_HOVER', payload: true });
    const handleMouseLeave = () => dispatch({ type: 'SET_HOVER', payload: false });
    const handleMouseDown = () => dispatch({ type: 'SET_PRESSED', payload: true });
    const handleMouseUp = () => dispatch({ type: 'SET_PRESSED', payload: false });

    return (
        <button
            type="button"
            className={`w-full text-left px-4 py-2 text-sm ${themeColors.text.primary} hover:${themeColors.surface.light} ${className}
                ${state.isHovered ? 'bg-opacity-90' : ''}
                ${state.isPressed ? 'transform scale-[0.98]' : ''}
            `}
            onClick={onClick}
            role="menuitem"
            title={title}
            data-testid={dataTestId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
            {children}
        </button>
    );
};

DropdownItem.propTypes = {
    onClick: PropTypes.func,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    title: PropTypes.string,
    'data-testid': PropTypes.string
};

const Dropdown = ({
    isOpen,
    onClose,
    children,
    className = '',
    align = 'left', // 'left', 'right', or 'center'
    width = 'w-48',
    portal,
    anchorRef,
    'data-testid': dataTestId
}) => {
    const dropdownRef = useRef(null);
    const [portalPosition, setPortalPosition] = useState(null);
    const isPortalled = portal === true;

    useEffect(() => {
        const handleClickOutside = (event) => {
            // If the click is on a button with data-testid="export-batch-items-button", ignore it
            if (event.target.closest('[data-testid="export-batch-items-button"]')) {
                return;
            }
            
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !anchorRef?.current?.contains(event.target)) {
                onClose();
            }
        };

        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [anchorRef, isOpen, onClose]);

    useLayoutEffect(() => {
        if (!isPortalled || !isOpen) {
            setPortalPosition(null);
            return undefined;
        }

        if (!(anchorRef?.current instanceof HTMLElement)) {
            throw new Error('Portalled Dropdown requires an anchor element.');
        }

        const updatePortalPosition = () => {
            const anchorBounds = anchorRef.current.getBoundingClientRect();
            const left = align === 'right'
                ? anchorBounds.right
                : align === 'center'
                    ? anchorBounds.left + (anchorBounds.width / 2)
                    : anchorBounds.left;
            setPortalPosition({
                top: anchorBounds.bottom + 4,
                left,
                // A left-aligned panel grows rightward from the anchor, so shift it left by however far it would overflow the viewport (100% is the panel's own width).
                ...(align === 'left' && { transform: `translateX(min(0px, calc(100vw - ${left + PORTAL_VIEWPORT_MARGIN}px - 100%)))` })
            });
        };

        updatePortalPosition();
        window.addEventListener('resize', updatePortalPosition);
        window.addEventListener('scroll', updatePortalPosition, true);

        return () => {
            window.removeEventListener('resize', updatePortalPosition);
            window.removeEventListener('scroll', updatePortalPosition, true);
        };
    }, [align, anchorRef, isOpen, isPortalled]);

    if (!isOpen) return null;

    const alignmentClasses = align === 'right' 
        ? 'right-0' 
        : align === 'center'
            ? 'left-1/2 -translate-x-1/2'
            : 'left-0';

    if (isPortalled && portalPosition === null) {
        return null;
    }

    const portalAlignmentClasses = align === 'right'
        ? '-translate-x-full'
        : align === 'center'
            ? '-translate-x-1/2'
            : '';

    const dropdown = (
        <div 
            ref={dropdownRef}
            className={`${isPortalled ? `fixed ${portalAlignmentClasses} z-[100000001]` : `absolute top-full ${alignmentClasses} mt-1 z-[99999]`} ${width} rounded-md shadow-lg ${themeColors.surface.DEFAULT} ring-1 ring-black ring-opacity-5 ${className} animate-dropdown-in`}
            style={isPortalled ? portalPosition : undefined}
            role="menu"
            tabIndex={-1}
            aria-orientation="vertical"
            onClick={(e) => e.stopPropagation()}
            data-testid={dataTestId}
        >
            <div className="py-1">
                {children}
            </div>
        </div>
    );

    return isPortalled ? createPortal(dropdown, document.body) : dropdown;
};

Dropdown.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    align: PropTypes.oneOf(['left', 'right', 'center']),
    width: PropTypes.string,
    portal: PropTypes.bool,
    anchorRef: PropTypes.shape({
        current: PropTypes.instanceOf(HTMLElement)
    }),
    'data-testid': PropTypes.string
};

export { Dropdown, DropdownItem }; 
